import { CalendarIcon, XIcon } from 'lucide-react';
import {
  DatePicker as AriaDatePicker,
  DatePickerProps as AriaDatePickerProps,
  DateRangePicker as AriaDateRangePicker,
  DateRangePickerProps as AriaDateRangePickerProps,
  DateValue as AriaDateValue,
  Dialog as AriaDialog,
  DialogProps as AriaDialogProps,
  PopoverProps as AriaPopoverProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
} from 'react-aria-components';

import { useTranslations } from '../../../i18n/useTranslations';
import { cn } from '../../../lib/stylingUtils';
import { Button } from '../buttons/Button';
import { FieldError, FieldGroup, Label } from '../Field';
import { Popover } from '../overlays/Popover';

import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
  RangeCalendar,
} from './Calendar';
import { DateInput, DateSegment } from './DateField';

const DatePickerContent = ({
  className,
  popoverClassName,
  ...props
}: AriaDialogProps & { popoverClassName?: AriaPopoverProps['className'] }) => (
  <Popover
    className={composeRenderProps(
      cn('@DatePickerContent', popoverClassName),
      (className) => cn('w-auto p-3', className),
    )}
  >
    <AriaDialog
      className={cn(
        'flex w-full flex-col space-y-4 outline-none sm:flex-row sm:space-y-0 sm:space-x-4',
        className,
      )}
      {...props}
    />
  </Popover>
);

interface DatePickerProps<
  T extends AriaDateValue,
> extends AriaDatePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
}

const DatePicker = <T extends AriaDateValue>({
  className,
  label,
  description,
  errorMessage,
  ...props
}: DatePickerProps<T>) => {
  const t = useTranslations();

  return (
    <AriaDatePicker
      className={composeRenderProps(className, (className) =>
        cn('@DatePicker', 'group flex flex-col gap-2', className),
      )}
      {...props}
    >
      <Label aria-required={props.isRequired}>{label}</Label>
      <FieldGroup className="gap-2 pr-1">
        <DateInput className="flex-1">
          {(segment) => <DateSegment segment={segment} />}
        </DateInput>
        <Button
          aria-label={t.components.datePicker.openCalendar}
          variant="text"
          className="size-5 p-0"
          size="icon"
          icon={<CalendarIcon aria-hidden />}
        />
      </FieldGroup>
      {description && (
        <Text className="text-sm text-neutral/20" slot="description">
          {description}
        </Text>
      )}
      <FieldError>{errorMessage}</FieldError>
      <DatePickerContent>
        <Calendar>
          <CalendarHeading />
          <CalendarGrid>
            <CalendarGridHeader>
              {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
            </CalendarGridHeader>
            <CalendarGridBody>
              {(date) => <CalendarCell date={date} />}
            </CalendarGridBody>
          </CalendarGrid>
        </Calendar>
      </DatePickerContent>
    </AriaDatePicker>
  );
};

interface DateRangePickerProps<
  T extends AriaDateValue,
> extends AriaDateRangePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  clearable?: boolean;
  onClear?: () => void;
}

const DateRangePicker = <T extends AriaDateValue>({
  className,
  label,
  description,
  errorMessage,
  clearable,
  onClear,
  ...props
}: DateRangePickerProps<T>) => {
  const t = useTranslations();
  const hasValue = props.value?.start && props.value?.end;

  return (
    <AriaDateRangePicker
      aria-label={'Pick a date'}
      className={composeRenderProps(
        cn('@DateRangePicker', 'h-input min-w-[240px] shrink-0', className),
        (className) => cn('group flex flex-col gap-2', className),
      )}
      {...props}
    >
      {label && <Label>{label}</Label>}
      <FieldGroup className="gap-2 pr-1">
        <DateInput slot={'start'}>
          {(segment) => <DateSegment segment={segment} />}
        </DateInput>
        <span aria-hidden className="text-sm text-neutral/20">
          -
        </span>
        <DateInput className="flex-1" slot={'end'}>
          {(segment) => <DateSegment segment={segment} />}
        </DateInput>

        {clearable && hasValue && onClear ? (
          <Button
            aria-label={t.components.datePicker.clear}
            variant="text"
            className="size-6 p-1"
            size="icon"
            onPress={onClear}
            icon={<XIcon aria-hidden />}
          />
        ) : (
          <Button
            aria-label={t.components.datePicker.openCalendar}
            variant="text"
            className="size-6 p-1"
            size="icon"
            icon={<CalendarIcon aria-hidden />}
          />
        )}
      </FieldGroup>
      {description && (
        <Text className="text-sm text-neutral/20" slot="description">
          {description}
        </Text>
      )}
      <FieldError>{errorMessage}</FieldError>
      <DatePickerContent>
        <RangeCalendar>
          <CalendarHeading />
          <CalendarGrid>
            <CalendarGridHeader>
              {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
            </CalendarGridHeader>
            <CalendarGridBody>
              {(date) => <CalendarCell date={date} />}
            </CalendarGridBody>
          </CalendarGrid>
        </RangeCalendar>
      </DatePickerContent>
    </AriaDateRangePicker>
  );
};

export { DatePicker, DatePickerContent, DateRangePicker };
export type { DatePickerProps, DateRangePickerProps };
